import React, { useEffect, useState, useContext } from "react";
import { AppContext } from "../context/AppContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import moment from "moment";
import { getLogData } from "../services/adminServices";
import { CONFIG } from "../config/general";

const WarningLogs = () => {
  const { login } = useContext(AppContext);

  const [filters, setFilters] = useState({
    userId: "",
    appName: "",
    startDate: null,
    endDate: null,
    key: "accessedAt", // default sort key
    direction: "desc", // default sort direction
    page: 1,
    limit: 50,
  });

  const [warnings, setWarnings] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalRecords: 0,
  });
  const [loading, setLoading] = useState(false);

  // ✅ Format date for backend query
  const formatDate = (date, isStart) => {
    if (!date) return null;
    const d = new Date(date);
    if (isStart) d.setHours(0, 0, 0, 0);
    else d.setHours(23, 59, 59, 999);
    return d.toISOString();
  };

  // ✅ Fetch Warnings from backend
  const getWarnings = async (filter = filters) => {
    try {
      setLoading(true);

      const params = {
        page: filter.page,
        limit: filter.limit,
        key: filter.key,
        direction: filter.direction,
        ...(filter.userId && { userId: filter.userId }),
        ...(filter.appName && { appName: filter.appName }),
        ...(filter.startDate && { startDate: formatDate(filter.startDate, true) }),
        ...(filter.endDate && { endDate: formatDate(filter.endDate, false) }),
      };

      const res = await getLogData(login?.token, params);

      if (res.success) {
        setWarnings(res.data || []);
        setPagination({
          currentPage: res.pagination?.currentPage || 1,
          totalPages: res.pagination?.totalPages || 0,
          totalRecords: res.pagination?.totalRecords || 0,
        });
      } else {
        toast.error(res.message || "Failed to load warning logs");
      }
    } catch (error) {
      console.error("Error fetching warnings:", error);
      toast.error("Server error fetching warnings");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle input/filter changes
  const handleChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value, page: 1 }));
  };

  // ✅ Sorting toggle
  const handleSort = (key) => {
    setFilters((prev) => ({
      ...prev,
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
      page: 1,
    }));
  };

  // ✅ Pagination Controls
  const handleNext = () => {
    if (pagination.currentPage < pagination.totalPages) {
      const newPage = pagination.currentPage + 1;
      setFilters((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handlePrev = () => {
    if (pagination.currentPage > 1) {
      const newPage = pagination.currentPage - 1;
      setFilters((prev) => ({ ...prev, page: newPage }));
    }
  };

  // ✅ Re-fetch on filter/page change
  useEffect(() => {
    getWarnings(filters);
  }, [filters.page, filters.limit, filters.key, filters.direction]);

  // ✅ Initial fetch
  useEffect(() => {
    getWarnings();
  }, []);

  return (
    <div className="details-table mt-3">
      {/* ---------------- Filters ---------------- */}
      <div
        className="filters mb-3 p-3 border rounded"
        style={{ backgroundColor: "var(--card-background)" }}
      >
        <div className="row align-items-end">
          {/* App Name Filter */}
          <div className="col-md-3 mb-2">
            <label className="form-label fw-bold">App Name:</label>
            <input
              type="text"
              name="appName"
              value={filters.appName}
              onChange={handleChange}
              className="form-control"
              placeholder="Search by app name"
            />
          </div>

          {/* Date Range Filter */}
          <div className="col-md-4 mb-2">
            <label className="form-label fw-bold">Date Range:</label>
            <DatePicker
              selectsRange
              startDate={filters.startDate}
              endDate={filters.endDate}
              onChange={(update) =>
                setFilters((prev) => ({
                  ...prev,
                  startDate: update[0],
                  endDate: update[1],
                  page: 1,
                }))
              }
              className="form-select"
              placeholderText="Select date range"
            />
          </div>

          {/* Limit Filter */}
          <div className="col-md-3 mb-2">
            <label className="form-label fw-bold">Results Per Page:</label>
            <select
              className="form-select"
              value={filters.limit}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, limit: e.target.value, page: 1 }))
              }
            >
              {[25, 50, 100, 200].map((val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
            </select>
          </div>

          {/* Apply Filter */}
          <div className="col-md-2 mb-2 text-end">
            <button
              className="btn btn-success w-100"
              onClick={() => getWarnings(filters)}
              disabled={loading}
            >
              {loading ? "Loading..." : "Apply"}
            </button>
          </div>
        </div>
      </div>
{console.log(pagination)}
      {/* ---------------- Table Section ---------------- */}
      {!loading ? (
        <>
          {/* Pagination Header */}
          <div className="custom-pagination d-flex justify-content-between align-items-center mb-2">
            <span>
              Showing{" "}
              {pagination.totalRecords > 0
                ? `${(pagination.currentPage - 1) * filters.limit + 1} -
                 ${Math.min(pagination.currentPage * filters.limit, pagination.totalRecords)}`
                : 0}{" "}
              of {pagination.totalRecords || 0}
            </span>

            <div className="page-controls d-flex align-items-center">
              <button
                className="btn btn-outline-secondary btn-sm me-2"
                onClick={handlePrev}
                disabled={pagination.currentPage === 1}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <span className="mx-2">{pagination.currentPage}</span>
              <span>/</span>
              <span className="mx-2">{pagination.totalPages}</span>
              <button
                className="btn btn-outline-secondary btn-sm ms-2"
                onClick={handleNext}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>

          {/* Data Table */}
          <div className="table-responsive">
            <table className="table table-hover table-bordered align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("user")}
                  >
                    User{" "}
                    {filters.key === "user" &&
                      (filters.direction === "asc" ? "▲" : "▼")}
                  </th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("appName")}
                  >
                    App Name{" "}
                    {filters.key === "appName" &&
                      (filters.direction === "asc" ? "▲" : "▼")}
                  </th>
                  <th>Warning Message</th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("accessedAt")}
                  >
                    Accessed At{" "}
                    {filters.key === "accessedAt" &&
                      (filters.direction === "asc" ? "▲" : "▼")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {warnings.length > 0 ? (
                  warnings.map((w, i) => (
                    <tr key={w._id}>
                      <td>
                        {(pagination.currentPage - 1) * filters.limit + (i + 1)}
                      </td>
                      <td>{w.user?.name || "N/A"}</td>
                      <td>{w.appName || "-"}</td>
                      <td style={{ whiteSpace: "pre-wrap" }}>
                        {w.warningMessage || "-"}
                      </td>
                      <td>
                        {moment(w.accessedAt).format("DD-MM-YYYY HH:mm:ss")}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center text-muted">
                      No warnings found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="d-flex justify-content-center align-items-center p-5">
          <div className="heartbeat-loader">
            <img
              src={CONFIG.REACT_APP_UPLOADS + "/Icons/logo.png"}
              alt="loading"
              style={{ width: "80px" }}
              className="heartbeat-image"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WarningLogs;
